/**
 * HubMyTasksRail — the compact "My Tasks" glance at the top of the Hub right rail
 * (Workspace v3.x). The FULL agenda (all buckets + the recurrence picker) lives in
 * the Workspace "My Tasks" tab; this is a peek: Overdue (always) + the chosen
 * window (Today / This week), capped, with a quick one-off "+ add a task" and a
 * "View all in Workspace" link.
 *
 * Reuses listWorkItems({ for_user: me }) + the agenda's colour/glyph encoding
 * (sage dot = yours, gold ring + the owner's champagne avatar = shared, a Repeat
 * loop = recurring, a terracotta chip = overdue). Words ride aria-label, except
 * LP-16: a card you are only a collaborator on says "Shared with you" under its
 * row, with Leave, which removes your own collaborator row and nobody else's.
 *
 * The card never becomes tall dead space and never vanishes: when nothing's due
 * it collapses to a slim caught-up line that still carries the add affordance
 * (the deliberate exception to the rail's hide-when-empty rule). The window
 * choice persists per-user in localStorage (the fuller home preference is a
 * Settings concern, out of scope here).
 *
 * Founder walk (2026-07-06): a compact DERIVED "Needs your attention"
 * glance sits ABOVE the task list for clinical staff with attention
 * items — the Clients attention band and this rail are ONE system seen
 * from two doors, not two systems. HARD RULES: the glance NEVER creates
 * task rows (attention is derived, never hand-kept — the protected
 * no-create-task ruling) and every line links to the Clients landing
 * (the owning surface; no second confirm surface here). It reuses the
 * cmHome aggregate read (same query key as the Clients page — react-
 * query dedupes, so the two surfaces share ONE audited read within
 * staleTime), enabled only for read_clinical_record holders; everyone
 * else sees exactly the pre-existing rail.
 */

import { Fragment, useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { CLIENT_BUBBLE_CLASS } from '@/components/workspace/ClientBubble';
import { Repeat, Plus } from 'lucide-react';
import { useTenant } from '@/lib/auth';
import { useWorkItems, useCreateWorkItem, useRemoveCollaborator } from '@/lib/workspace';
import { canManageOwnTasks, canManageWorkItems, canReadClinicalRecord, canReadOwnWorkItems } from '@/lib/capabilities';
import { pageWriteText } from '@/lib/errorCopy';
import { useSupportContact } from '@/hooks/useSupportContact';
import { useCmHome } from '@/lib/cmHomeApi';
import { attentionCardTitle, orderAttention } from '@/lib/cmHome';
import { practiceTodayIso } from '@/lib/practiceDate';
import { bucketOf } from '@/components/workspace/workBuckets';
import { shortDate, isOverdue, initials } from '@/components/workspace/workspaceFormat';
import { SkeletonRow } from '@/components/Skeleton';
import type { WorkItemRow } from '@/lib/api';
import { HUB_MY_TASKS_WINDOW_KEY } from '@/lib/keptAcrossSignOut';
import { DateInput } from '@/components/primitives/DateInput';
import { FlooredTitle } from '@/components/workspace/FlooredTitle';
import { Button } from '@/components/primitives/Button';

type Win = 'today' | 'week';
const WINDOW_KEY = HUB_MY_TASKS_WINDOW_KEY;
const CAP = 5;
/** Compact glance: the rail shows at most this many attention lines. */
const ATTENTION_GLANCE_CAP = 3;

// LP-42: the glance opens on This week. A stored Today is the person's own
// choice and still wins; anything else, nothing stored included, is This week.
function readWindow(): Win {
  try {
    return window.localStorage.getItem(WINDOW_KEY) === 'today' ? 'today' : 'week';
  } catch {
    return 'week';
  }
}

function railTitle(t: WorkItemRow): string {
  return t.display_name || t.title || t.prospect_name || 'Untitled';
}

export function HubMyTasksRail(): ReactNode {
  const { whoami, tenantSlug } = useTenant();
  const userId = whoami?.user_id ?? '';
  // view_staff_tools OR view_workspace — mirrors the self-scoped exception
  // the API grants ?for_user=<self> (workItems.ts listWorkItems). A caller
  // holding neither (e.g. mso_staff) never issues this request.
  const canReadTasks = canReadOwnWorkItems(whoami);
  // LP-2: the add box belongs to whoever can save a task: the office
  // (manage_work_items) or an own-tasks holder (manage_own_tasks). A seat
  // that can only read its tasks (the board seat) gets the list, no box.
  const canAdd = canManageWorkItems(whoami) || canManageOwnTasks(whoami);
  const supportContact = useSupportContact({ enabled: canReadTasks });
  const agendaQ = useWorkItems(
    tenantSlug,
    { for_user: userId, limit: 100 },
    { enabled: userId !== '' && canReadTasks },
  );
  const createTask = useCreateWorkItem(tenantSlug);
  // LP-16: Leave on a card shared with you removes your own collaborator row.
  const leaveShared = useRemoveCollaborator(tenantSlug);

  // The derived attention glance (founder walk 2026-07-06) — clinical
  // staff only; the hook runs unconditionally (enabled-gated) so no
  // hooks sit below a return (the #310 class).
  const clinical = canReadClinicalRecord(whoami);
  const todayIso = useMemo(() => practiceTodayIso(), []);
  const attentionQ = useCmHome(tenantSlug, todayIso, {
    enabled: clinical && tenantSlug !== '',
  });
  const attention = useMemo(
    () => (clinical ? orderAttention(attentionQ.home?.attention ?? []) : []),
    [clinical, attentionQ.home],
  );
  const [win, setWin] = useState<Win>(() => readWindow());
  const [adding, setAdding] = useState(false);
  const [newTask, setNewTask] = useState('');
  const [dueDate, setDueDate] = useState(''); // optional due date for the one-off task

  // A caller who can read neither their own tasks nor the Workspace board
  // (no view_staff_tools, no view_workspace — e.g. mso_staff) has nothing
  // this rail can show: no fetch was even issued above. Hide the whole
  // card rather than surface a load error for a request that was never
  // going to succeed. Checked after every hook above has run
  // unconditionally (the #310 class), same discipline as the attention
  // glance's enabled-gated hook.
  if (!canReadTasks) return null;

  function selectWindow(w: Win): void {
    setWin(w);
    try {
      window.localStorage.setItem(WINDOW_KEY, w);
    } catch {
      /* private mode / storage disabled — the in-memory choice still holds */
    }
  }

  // Overdue (always) + the chosen window. Today shows in both windows; Upcoming
  // is never in the glance (the full agenda has it).
  function inWindow(t: WorkItemRow): boolean {
    const b = bucketOf(t);
    if (b === 'Overdue' || b === 'Today') return true;
    return win === 'week' && b === 'This week';
  }
  const open = agendaQ.rows.filter((r) => r.status !== 'done');
  const visible = open.filter(inWindow).sort((a, b) => {
    const ao = isOverdue(a) ? 0 : 1;
    const bo = isOverdue(b) ? 0 : 1;
    if (ao !== bo) return ao - bo;
    return (a.due_date ?? '9999').localeCompare(b.due_date ?? '9999');
  });
  const shown = visible.slice(0, CAP);
  const overflow = visible.length - shown.length;

  const isLoading = userId === '' || agendaQ.isLoading;
  const isEmpty = !isLoading && !agendaQ.isError && visible.length === 0;

  function addTask(e: React.FormEvent): void {
    e.preventDefault();
    const title = newTask.trim();
    if (!title || createTask.isPending) return;
    createTask.mutate(
      { kind: 'task', title, assignee_user_id: userId, due_date: dueDate || null },
      {
        onSuccess: () => {
          setNewTask('');
          setDueDate('');
          setAdding(false);
        },
      },
    );
  }

  return (
    <section data-testid="hub-mytasks-rail" aria-label="My tasks" className="rounded-card bg-paper px-5 py-[18px] shadow-sh2">
      {/* The title row wraps: on a narrow card the window toggle sits
          under the title, and its pills wrap inside the frame. */}
      <div className="mb-3 flex flex-wrap items-center gap-2.5">
        <h3 className="flex-1 font-display text-[16px] font-semibold text-sage-800">My Tasks</h3>
        <div role="group" aria-label="Window" className="flex max-w-full flex-wrap rounded-pill bg-sand p-0.5 text-[11.5px] font-semibold">
          {(['today', 'week'] as const).map((w) => (
            <button
              key={w}
              type="button"
              data-testid={`hub-mytasks-rail-window-${w}`}
              aria-pressed={win === w}
              onClick={() => selectWindow(w)}
              className={'rounded-pill px-2.5 py-1 ' + (win === w ? 'bg-paper text-sage-800 shadow-sh1' : 'text-sage-400')}
            >
              {w === 'today' ? 'Today' : 'This week'}
            </button>
          ))}
        </div>
      </div>

      {/* The add affordance is ALWAYS present for anyone who may add, even
          when caught up: the rail's inline entry point (this card's deliberate
          never-dead, never-gone rule). It sits first, under the title and
          above every row, the filled house Button the My Clients card and the
          Workspace tab carry. One-off only; the recurrence picker stays in
          the Workspace tab. The open form takes the title on its own line,
          then the date, with Add and Cancel after every field. A refused save
          says so in a sentence and keeps what was typed. */}
      {!canAdd ? null : adding ? (
        <form className="mb-3 space-y-1.5" onSubmit={addTask}>
          <input
            value={newTask}
            onChange={(e) => setNewTask(e.target.value)}
            placeholder="What needs doing?"
            aria-label="Add a task"
            data-testid="hub-mytasks-rail-add-input"
            autoFocus
            className="w-full rounded-base border border-sage-200 bg-paper px-2.5 py-1.5 text-[13px] text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
          />
          <DateInput
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
            aria-label="Due date"
            data-testid="hub-mytasks-rail-add-due"
            size="compact" className="w-full"
          />
          {createTask.isError && (
            <p role="alert" data-testid="hub-mytasks-rail-add-error" className="text-[12px] text-pepper">
              {pageWriteText(createTask.error, supportContact)}
            </p>
          )}
          <div className="flex items-center gap-1.5 pt-0.5" data-testid="hub-mytasks-rail-add-actions">
            <button
              type="submit"
              disabled={createTask.isPending || newTask.trim() === ''}
              data-testid="hub-mytasks-rail-add-submit"
              className="rounded-base bg-sage-800 px-2.5 py-1.5 text-[13px] font-semibold text-cream disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
            >
              Add
            </button>
            <button
              type="button"
              onClick={() => {
                setAdding(false);
                setNewTask('');
                setDueDate('');
              }}
              data-testid="hub-mytasks-rail-add-cancel"
              className="rounded-base px-2 py-1.5 text-[13px] font-medium text-ink-soft hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
            >
              Cancel
            </button>
          </div>
        </form>
      ) : (
        <Button variant="primary" size="sm" icon={<Plus size={15} aria-hidden="true" />} className="mb-3" data-testid="hub-mytasks-rail-add" onClick={() => setAdding(true)}>
          Add a task
        </Button>
      )}

      {/* The derived attention glance — rendered only when the caller HAS
          clinical attention items. Lines are LINKS to the Clients landing
          (the owning surface); nothing here creates or keeps task rows.
          Gold ground: gold means waiting-on-you (the design law). */}
      {attention.length > 0 && (
        <div
          data-testid="hub-attention-glance"
          className="mb-3 rounded-base bg-gold-soft/60 px-2.5 py-2"
        >
          <p className="mb-1 text-[10.5px] font-semibold uppercase tracking-[0.12em] text-caution-deep">
            Needs your attention
          </p>
          <div className="space-y-0.5">
            {attention.slice(0, ATTENTION_GLANCE_CAP).map((card) => (
              <Link
                key={card.note_id + card.kind}
                to={`/t/${tenantSlug}/clients`}
                data-testid="hub-attention-line"
                className="block truncate rounded-base px-1 py-0.5 text-[12.5px] font-medium text-ink hover:bg-gold-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
              >
                {attentionCardTitle(card, todayIso)}
              </Link>
            ))}
            {attention.length > ATTENTION_GLANCE_CAP && (
              <Link
                to={`/t/${tenantSlug}/clients`}
                data-testid="hub-attention-more"
                className="block px-1 pt-0.5 text-[11.5px] font-semibold text-caution-deep hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
              >
                +{attention.length - ATTENTION_GLANCE_CAP} more on Clients
              </Link>
            )}
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="space-y-2"><SkeletonRow /><SkeletonRow /></div>
      ) : agendaQ.isError ? (
        <p className="py-1 text-[12.5px] text-pepper">Could not load your tasks.</p>
      ) : isEmpty ? (
        <p data-testid="hub-mytasks-rail-empty" className="py-1 text-[12.5px] text-ink-soft">You’re all caught up.</p>
      ) : (
        <div className="space-y-1.5">
          {shown.map((t) => {
            const shared = t.my_role === 'collaborator';
            const overdue = isOverdue(t);
            const recurring = t.recurrence_id !== null;
            const isTask = t.kind === 'task';
            return (
              <Fragment key={t.id}>
              {/* LP-43: title, client, date. The row link stretches over the
                  whole row (its ::after), so the date and the blank space still
                  open the task; the client's name sits above it and opens the
                  record. The title gives way first (down to its own width or
                  8rem, FlooredTitle), then the name; the date never does, and
                  sits flush right so the dates line up row to row. */}
              <div className="relative flex items-center gap-1.5 rounded-base px-1.5 py-1 hover:bg-sand">
              <Link
                to={`/t/${tenantSlug}/clients?item=${t.id}`}
                data-testid="hub-mytasks-rail-row"
                data-role={shared ? 'collaborator' : 'owner'}
                className="flex flex-1 items-center gap-2.5 after:absolute after:inset-0 after:rounded-base after:content-[''] focus-visible:outline-none focus-visible:after:ring-2 focus-visible:after:ring-sage-400"
              >
                <span className="flex w-3.5 flex-shrink-0 items-center justify-center">
                  {shared ? (
                    <span aria-hidden="true" className="h-2.5 w-2.5 rounded-full border-2 border-warm-400" />
                  ) : (
                    <span aria-label="Yours" title="Yours" className="h-2.5 w-2.5 rounded-full bg-sage-400" />
                  )}
                </span>
                {recurring && (
                  <Repeat size={11} aria-hidden="true" data-testid="hub-mytasks-rail-recurring" className="flex-shrink-0 text-sage-500" />
                )}
                <FlooredTitle
                  text={railTitle(t)}
                  className={'text-[13px] ' + (isTask ? 'font-medium text-ink' : 'font-display font-semibold text-sage-800')}
                />
                {shared && (
                  <span
                    data-testid="hub-mytasks-rail-owner-avatar"
                    aria-label={t.assignee_name ? `Owned by ${t.assignee_name}` : 'No owner yet'}
                    title={t.assignee_name ? `Owned by ${t.assignee_name}` : 'No owner yet'}
                    className="inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-gold-soft text-[10px] font-semibold text-warm-700"
                  >
                    {initials(t.assignee_name)}
                  </span>
                )}
              </Link>
              {/* LP-2: the client the task is about, a link to the record.
                  LP-43: the name in the client bubble, in the date pill's family
                  a step deeper, so it reads apart from the title. */}
              {isTask && t.client_id && t.client_display_name && (
                <Link
                  to={`/t/${tenantSlug}/clients/${t.client_id}`}
                  data-testid={`hub-mytasks-rail-client-${t.id}`}
                  title={t.client_display_name}
                  className={`relative z-10 min-w-0 truncate text-[0.8125rem] ${CLIENT_BUBBLE_CLASS}`}
                >
                  {t.client_display_name}
                </Link>
              )}
              {t.due_date && (
                <span
                  data-testid={`hub-mytasks-rail-due-${t.id}`}
                  className={'ml-auto flex-shrink-0 whitespace-nowrap text-[11px] font-semibold ' + (overdue ? 'rounded-pill bg-pepper-soft px-2 py-0.5 text-pepper' : 'text-sage-400')}
                >
                  {shortDate(t.due_date)}
                </span>
              )}
              </div>
              {shared && (
                <div className="flex flex-wrap items-center gap-x-2 pl-7 text-[0.71875rem] text-ink-soft">
                  <span data-testid={`hub-mytasks-rail-shared-${t.id}`}>Shared with you</span>
                  {canAdd && (
                    <Button
                      variant="ghost"
                      size="sm"
                      data-testid={`hub-mytasks-rail-leave-${t.id}`}
                      aria-label={`Leave ${railTitle(t)}`}
                      loading={leaveShared.isPending && leaveShared.variables?.id === t.id}
                      disabled={leaveShared.isPending}
                      onClick={() => leaveShared.mutate({ id: t.id, userId })}
                    >
                      Leave
                    </Button>
                  )}
                  {leaveShared.isError && leaveShared.variables?.id === t.id && (
                    <p role="alert" data-testid={`hub-mytasks-rail-leave-error-${t.id}`} className="basis-full text-xs text-pepper">
                      {pageWriteText(leaveShared.error, supportContact)}
                    </p>
                  )}
                </div>
              )}
              </Fragment>
            );
          })}
          {overflow > 0 && <p className="px-1.5 pt-0.5 text-[11.5px] text-sage-400">+{overflow} more</p>}
        </div>
      )}

      <div className="mt-1">
        <Link
          to={`/t/${tenantSlug}/clients#your-day-tasks`}
          data-testid="hub-mytasks-rail-viewall"
          className="inline-flex items-center gap-1 rounded-base px-1.5 text-[12px] font-semibold text-sage-600 hover:text-sage-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
        >
          View all on My Clients
          <span aria-hidden="true">→</span>
        </Link>
      </div>
    </section>
  );
}
